import { CustomCalendarType, SimpleActivity } from "@ac-expo/api-types";
import { FileType } from "@prisma/client";
import { usePlanning } from "./use-planning";
import { EventSchedulesTimes, PlanningEvent } from "./utils";
import { VerticalByPlaceCalendar } from "./VerticalByPlaceCalendar";
import { VerticalWeeklyCalendar } from "./VerticalWeeklyCalendar";
import { FullCalendarView } from "./FullCalendarView";

interface PlanningComponentProps {
  activities: SimpleActivity[];
  eventSchedules?: EventSchedulesTimes[];
  hideEmptyDays?: boolean;
  isRegisterEnabled?: boolean;
  onOpenActivitiesDetails?: (events: PlanningEvent[]) => void;
  customCalendar?: CustomCalendarType;
  getFileUrl?: (key?: string, type?: FileType, width?: number) => string;
}

const defaultGetFileUrl = () => "";

const PlanningComponent = ({
  isRegisterEnabled,
  activities,
  eventSchedules,
  hideEmptyDays = true,
  onOpenActivitiesDetails,
  customCalendar,
  getFileUrl = defaultGetFileUrl,
}: PlanningComponentProps) => {
  const {
    groupedEvents,
    groupedByWeek,
    minTime,
    maxTime,
    setPlaceFilter,
    allDaysWithActivities,
    groupedPlaces,
    handlePrev,
    filteredSlots,
    mergedSlotsForHorizontal,
    handleNext,
    calendarRef,
    visibleDays,
    setVisibleDays,
    isFirstDay,
    isLastDay,
    isFirstMonth,
    isLastMonth,
    currentDay,
  } = usePlanning({
    activities,
    eventSchedules,
    hideEmptyDays,
    customCalendarModel: customCalendar?.model,
  });

  const backgroundStyleOptions = {
    backgroundPictureUrl: customCalendar?.backgroundPicture
      ? getFileUrl(customCalendar.backgroundPicture.key, "media")
      : undefined,
    backgroundColor: customCalendar?.backgroundColor,
    opacity: customCalendar?.opacity,
  };

  if (customCalendar?.model === "verticalByPlace") {
    return (
      <VerticalByPlaceCalendar
        allDaysWithActivities={allDaysWithActivities}
        filteredSlots={filteredSlots}
        groupedPlaces={groupedPlaces}
        setPlaceFilter={setPlaceFilter}
        onOpenActivitiesDetails={onOpenActivitiesDetails}
        isRegisterEnabled={isRegisterEnabled}
        customCalendar={customCalendar}
        backgroundStyleOptions={backgroundStyleOptions}
      />
    );
  }

  if (customCalendar?.model === "verticalWeekly") {
    return (
      <VerticalWeeklyCalendar
        groupedByWeek={groupedByWeek}
        filteredSlots={filteredSlots}
        groupedPlaces={groupedPlaces}
        currentDay={currentDay}
        isFirstMonth={isFirstMonth}
        isLastMonth={isLastMonth}
        handlePrev={handlePrev}
        handleNext={handleNext}
        setPlaceFilter={setPlaceFilter}
        onOpenActivitiesDetails={onOpenActivitiesDetails}
        isRegisterEnabled={isRegisterEnabled}
        customCalendar={customCalendar}
        backgroundStyleOptions={backgroundStyleOptions}
      />
    );
  }

  return (
    <FullCalendarView
      filteredSlots={filteredSlots}
      groupedEvents={groupedEvents}
      mergedSlotsForHorizontal={mergedSlotsForHorizontal}
      groupedPlaces={groupedPlaces}
      visibleDays={visibleDays}
      currentDay={currentDay}
      minTime={minTime}
      maxTime={maxTime}
      isFirstDay={isFirstDay}
      isLastDay={isLastDay}
      handlePrev={handlePrev}
      handleNext={handleNext}
      setPlaceFilter={setPlaceFilter}
      setVisibleDays={setVisibleDays}
      calendarRef={calendarRef}
      onOpenActivitiesDetails={onOpenActivitiesDetails}
      isRegisterEnabled={isRegisterEnabled}
      customCalendar={customCalendar}
      backgroundStyleOptions={backgroundStyleOptions}
    />
  );
};

export default PlanningComponent;
